import React from 'react'
import { navigate } from 'gatsby'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { ContactFormSchoolInput } from '../ContactFormSchoolInput'
import { ContactFormSchoolConfirm } from '../ContactFormSchoolConfirm'
import { mailFormData } from '@spin-dd/gatsby-theme-fujimoto/src/utils'

// 希望される資料
const inquiryTypes = [
  '学生募集要項',
  'パンフレット',
  'お問い合わせのみ（資料不要）',
]

// フォーム入力項目判定関数
const requireAddress = (inquiryTypes) =>
  inquiryTypes.includes('学生募集要項') || inquiryTypes.includes('パンフレット')
const requireGrade = (graduatedType) => graduatedType === '在学中'

const scheme = yup
  .object({
    inquiryType: yup.array().min(1, '希望される資料を選択してください'),
    nameKanji: yup
      .string()
      .max(60, '入力可能な文字数を超えています')
      .required('お名前（漢字）を入力してください'),
    nameFurigana: yup
      .string()
      .max(60, '入力可能な文字数を超えています')
      .required('お名前（フリガナ）を入力してください'),
    relationship: yup
      .string()
      .typeError('選択してください')
      .required('選択してください'),
    schoolName: yup
      .string()
      .max(100, '入力可能な文字数を超えています')
      .required('学校名を入力してください'),
    graduatedType: yup.string().required('選択してください'),
    grade: yup.string().when('graduatedType', (val) => {
      if (requireGrade(val)) {
        return yup.string().required('選択してください')
      }
      return yup.string().notRequired()
    }),
    postalCode: yup.string().when('inquiryType', (val) => {
      if (requireAddress(val)) {
        return yup
          .string()
          .matches(/^[0-9]{3}-?[0-9]{4}$/, '郵便番号（半角）を入力してください')
      }
      return yup.string().notRequired()
    }),
    prefecture: yup.string().when('inquiryType', (val) => {
      if (requireAddress(val)) {
        return yup.string().required('都道府県を選択してください')
      }
      return yup.string().notRequired()
    }),
    address1: yup.string().when('inquiryType', (val) => {
      if (requireAddress(val)) {
        return yup
          .string()
          .max(50, '入力可能な文字数を超えています')
          .required('ご住所を入力してください')
      }
      return yup.string().notRequired()
    }),
    address2: yup.string().when('inquiryType', (val) => {
      if (requireAddress(val)) {
        return yup.string().max(100, '入力可能な文字数を超えています')
      }
      return yup.string().notRequired()
    }),
    tel: yup
      .string()
      .max(21, '入力可能な文字数を超えています')
      .matches(/^[0-9]+-?[0-9]+-?[0-9]+$/, '電話番号を入力してください'),
    email: yup
      .string()
      .email('メールアドレスを入力してください')
      .required('メールアドレスを入力してください'),
    emailConfirm: yup
      .string()
      .email('メールアドレスを入力してください')
      .required('メールアドレスを入力してください')
      .test('match', 'メールアドレスが一致しません', function (email) {
        return email === this.parent.email
      }),
    inquiryBody: yup
      .string()
      .max(1000, '入力可能な文字数を超えています')
      .required('お問い合わせ内容を入力してください'),
    'g-recaptcha-response': yup
      .string()
      .required('reCAPTCHAをチェックしてください'),
  })
  .required()

function CustomContactFormSchool({
  data,
  // entry からテンプレートを抽出
  entry: { body } = {},
  // Component props
  // お問い合わせ受付 API
  apiPath = '/index.php',
  // 問い合わせ完了後の遷移先ページパス
  completePath = '/contact/complete/',
  // お問い合わせエラー時の遷移先ページパス
  errorPath = '/contact/error/',
  // フォーム内「個人情報保護方針」リンク先
  privacyPolicyPath = 'http://www.fujimoto.com/privacy/',
  // 「採用についてのお問い合わせはこちらからお問い合わせください」リンク先
  recruitmentPath = 'https://www.fujimoto.com/recruit/reccontact/',
  // h2 タイトルに付与するクラス
  className = '',
  // メール連携
  formId = 'default',
  subject = 'ホームページから問い合わせがありました（専門学校）',
}) {
  const [isConfirmed, setIsConfirmed] = React.useState(false)
  const methods = useForm({
    resolver: yupResolver(scheme),
    defaultValues: { inquiryType: [], graduatedType: '', grade: '' },
  })

  const onSubmit = (confirmedData) => {
    const formData = mailFormData({
      formId,
      subject,
      templateBody: body,
      data,
      confirmedData,
    })
    fetch(apiPath, {
      method: 'POST',
      body: formData,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.statusText)
        }
        navigate(completePath)
      })
      .catch(() => navigate(errorPath))
  }

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  // CSRFToken
  React.useEffect(() => {
    const fetchAndSetToken = async () => {
      await fetch(apiPath, {
        // 同一ドメインであることが前提
        mode: 'same-origin',
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(response.statusText)
          }
          return response.json()
        })
        .then(({ key }) => {
          methods.setValue('csrftoken', key)
        })
        .catch((error) => console.log({ error }))
    }
    fetchAndSetToken()
    // 初回時のみ実行する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <FormProvider {...methods}>
      {isConfirmed ? (
        <ContactFormSchoolConfirm
          onSubmit={onSubmit}
          onFix={() => {
            scrollToTop()
            // reCAPTCHA を revoke
            methods.setValue('g-recaptcha-response', '')
            setIsConfirmed(false)
          }}
          privacyPolicyPath={privacyPolicyPath}
          className={className}
          requireAddress={requireAddress}
        />
      ) : (
        <ContactFormSchoolInput
          onConfirm={() => {
            scrollToTop()
            setIsConfirmed(true)
          }}
          privacyPolicyPath={privacyPolicyPath}
          recruitmentPath={recruitmentPath}
          className={className}
          inquiryTypes={inquiryTypes}
          requireAddress={requireAddress}
          requireGrade={requireGrade}
        />
      )}
    </FormProvider>
  )
}

export { CustomContactFormSchool }
